<script setup>
import { ref, computed, inject, watchEffect, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'

import { ChevronLeftIcon } from '@heroicons/vue/24/outline'
import { ArrowRightIcon } from '@heroicons/vue/24/solid'

import ClassSummary from './components/ClassSummary.vue'
import HousingMealsSection from './components/HousingMealsSection.vue'
import PayWhatYouCan from './components/PayWhatYouCan.vue'

import { useClassStore } from '@/stores/ClassStore'
import { useInstructorStore } from '@/stores/InstructorStore'
import { useCartStore } from '@/stores/CartStore'
import { useUserStore } from '@/stores/UserStore'

import { cleanMarkup, capitalizeFirstLetter } from '@/helpers/text'

import { usDateFormatToShortMonthAndDay } from '@/helpers/datetime'
import { useTitle } from '../../hooks/useTitle'
import ShareBar from '@/components/ShareBar.vue'
import Form2 from '@/components/Form/Form2.vue'

const greaterOf = (a, b) => {
  if (a > b) return a
  return b
}

const props = defineProps(['classId'])

const siteConfig = inject('siteConfig')

const classStore = useClassStore()
const instructorStore = useInstructorStore()
const cartStore = useCartStore()
const userStore = useUserStore()
const ctaError = ref(null)

const router = useRouter()

console.log('router', router, router.currentRoute.fullPath)


const classDetails = ref(null)
const priceSelected = ref(false)

const currentLabel = computed(() => {
  if (!siteConfig.features?.separateClassesByDivision || !classDetails.value?.division) return 'classes'

  return Object.keys(siteConfig.features.separateClassesByDivision).find((key) =>
    siteConfig.features.separateClassesByDivision[key].includes(classDetails.value.division)
  )
})

onMounted(async () => {
  try {
    const details = await classStore.fetchClassById(props.classId)
    if (!details || details.cancelled) {
      router.push({ path: '/classes' })
      return // Add early return to prevent setting null details
    }
    classDetails.value = details
  } catch (error) {
    console.error('Error loading class details:', error)
    router.push({ path: '/classes' })
  }
})

const selectedImageIndex = ref(0)

const selectedImage = computed(() => {
  if (classDetails.value?.images) {
    return classDetails.value.images?.[selectedImageIndex.value]
  }
  return null
})

const instructors = computed(() => {
  let list = []

  if (classDetails.value?.instructorId) {
    list.push(instructorStore.instructorsById[classDetails.value.instructorId])
  }

  if (classDetails.value?.instructor2Id) {
    list.push(instructorStore.instructorsById[classDetails.value.instructor2Id])
  }

  // hack to solve race condition between loading class details and loading instructors
  // eslint-disable-next-line vue/no-side-effects-in-computed-properties
  // classDetails.value.instructorSummary = list.map((instructor) => instructor?.fullName).join(' and ')

  // Skip if there is no bio
  list = list.filter((instructor) => !!instructor?.bio)

  return list
})

const updateSelectedPayment = ($event) => {
  classDetails.value.costMember = $event.value
  classDetails.value.costNon = $event.value
  classDetails.value.fee = 0
  priceSelected.value = true
}

const addToCart = (id) => {
  const cartId = cartStore.addClass(id, { details: classDetails.value })

  if (siteConfig.key === 'snowfarm') {
    const imageUrl = `/sites/${siteConfig.key}/icon.png`

    cartStore.updateRegistrationFees()

    if (housingSelection.value) {
      let item = cartStore.itemCodeToItem(housingSelection.value.item_code)
      console.log(
        'item',
        item,
        'housingSelection.value.item_code',
        housingSelection.value.item_code,
      )
      const housingItem = {
        inventoryId: item.item_id,
        itemCode: item.item_code,
        glacct: item.glacct,
        title: `Housing – ${housingSelection.value.title}, Room ${housingSelection.value.num}`,
        subtitle: `For: ${classDetails.value.shortTitle}`,
        memberCost: housingSelection.value.price,
        nonMemberCost: housingSelection.value.price,
        imageUrl,
        parentId: cartId,
        removable: false,
        // hidePersonPicker: true
      }
      cartStore.addInventory(housingItem)

      item = cartStore.itemCodeToItem(mealSelection.value.item_code)
      console.log('item', item)
      const mealItem = {
        inventoryId: item.item_id,
        itemCode: item.item_code,
        glacct: item.glacct,
        title: `Meals – ${mealSelection.value.name}`,
        subtitle: `For: ${classDetails.value.shortTitle}`,
        memberCost: mealSelection.value.cost,
        nonMemberCost: mealSelection.value.cost,
        imageUrl,
        parentId: cartId,
        removable: false,
        // hidePersonPicker: true
      }
      cartStore.addInventory(mealItem)
      housingSelection.value = null
      mealSelection.value = null
    } else if (mealSelection.value) {
      let item = cartStore.itemCodeToItem(mealSelection.value.item_code)
      console.log(
        'item',
        item,
        'mealSelection.value.item_code',
        mealSelection.value.item_code,
      )
      const mealItem = {
        inventoryId: item.item_id,
        itemCode: item.item_code,
        glacct: item.glacct,
        title: `Meals – ${mealSelection.value.name}`,
        subtitle: `For: ${classDetails.value.shortTitle}`,
        memberCost: mealSelection.value.cost,
        nonMemberCost: mealSelection.value.cost,
        imageUrl,
        parentId: cartId,
        removable: false,
        // hidePersonPicker: true
      }
      cartStore.addInventory(mealItem)
      mealSelection.value = null
    }
  }

  window.scrollTo({ top: 0, behavior: 'smooth' })
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    cartStore.toggleCart(true)
  }, 50)
}

const addClassDayToCart = (id, scheduleId, classDetails) => {
  cartStore.addClassDay(id, scheduleId, classDetails)
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    cartStore.toggleCart(true)
  }, 50)
}

const addClassDaysToCart = (id, scheduleIds, classDetails) => {
  scheduleIds.forEach((scheduleId) => {
    cartStore.addClassDay(id, scheduleId, classDetails)
  })
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    cartStore.toggleCart(true)
  }, 50)
}

const addWaitlistClassDayToCart = (id, scheduleId) => {
  cartStore.addWaitlistClassDay(id, scheduleId)
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    cartStore.toggleCart(true)
  }, 50)
}

const addMakeItNowToCart = (id, classDays) => {
  console.log('addMakeItNowToCart', id, classDays)

  classDays.forEach((classDay) => {
    cartStore.addClassDay(id, classDay.scheduleId)
  })

  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    cartStore.toggleCart(true)
  }, 50)
}

const joinWaitlist = (id) => {
  cartStore.addWaitlistClass(id)
  setTimeout(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    cartStore.toggleCart(true)
  }, 50)
}

const isMakeItNow = computed(() => {
  let ok = siteConfig.key === 'pittsburgh'
  ok = ok && !!classDetails.value && !!classDetails.value.shortTitle
  ok = classDetails.value.shortTitle.toLowerCase().includes('make-it') || classDetails.value.shortTitle.toLowerCase().includes('gather and brew') || classDetails.value.shortTitle.toLowerCase().includes('fire and wine')
  ok = ok && classDetails.value.location === 'Hot Shop'
  ok = ok && hasOpenSlots.value > 0
  return ok
})

const isMakeItADate = computed(() => {
  let ok = siteConfig.key === 'pittsburgh'
  ok = ok && !!classDetails.value && !!classDetails.value.shortTitle
  ok = classDetails.value.shortTitle.toLowerCase().includes('make-it-a-date')
  ok = ok && classDetails.value.location === 'Hot Shop'
  return ok
})

const hasOpenSlots = computed(() => {
  if (!classDetails.value.daySales) return false

  let total = 0
  classDetails.value.classDays.forEach((day) => {
    if (day.limit - day.daysSold > 0) {
      total += 1
    }
  })
  return total > 0
})

const classParticipants = ref(null)
const showContactModal = ref(false)
const isLargeGroup = computed(() => {
  return classParticipants.value && classParticipants.value > 6
})

const checkParticipantsNumber = (e) => {
  if (classParticipants.value > 6) {
    showContactModal.value = true
    e.target.blur()
  }
}

const singleSelectedSlot = ref(null)

const daysWithSeatAvailability = (requiredSeats) => {
  // console.log('requiredSeats', requiredSeats)
  let list = []
  classDetails.value.classDays.forEach((day, index) => {
    // console.log('- day.dateStandard', day.dateStandard, day.startTimeFmt)
    const availableSeats = day.limit - day.daysSold

    if (availableSeats < 1) {
      // console.log(' - not ok, no available seats', day.dateStandard, day.startTimeFmt)
      return
    }

    const timeSlotsLeft = classDetails.value.classDays.length - (index + 1)
    // console.log('timeSlotsLeft', timeSlotsLeft)

    if (timeSlotsLeft < requiredSeats - 1) {
      // console.log(' - not enough timeslots left', day.dateStandard, day.startTimeFmt, timeSlotsLeft, requiredSeats - 1)
      return
    }

    let newEndTime = day.endTimeFmt
    let ok = true
    let days = [day]
    for (let i = 1; i < requiredSeats; i++) {
      let d = classDetails.value.classDays[index + i]
      let available = d.limit - d.daysSold
      ok = available >= 1
      if (ok) {
        days.push(d)
      }
      // console.log('d.endTimeFmt', d.endTimeFmt, available)
      newEndTime = d.endTimeFmt
    }

    if (ok && days.length == requiredSeats) {
      // console.log('index', index, 'classDays', days)
      // console.log(' - ok', day.startTimeFmt)
      list.push({
        date: day.dateFmt,
        day: day.classDay,
        time: `${day.startTimeFmt} - ${newEndTime}`,
        classDays: days,
        duration: (day.endTime - day.stTime) / 1000 / 60,
      })
    } else {
      // console.log(' - not ok', day.startTimeFmt)
    }
  })
  return list
}

const noSlotsAvailableMessage = computed(() => {
  const daysWithAtLeastOneSeat = daysWithSeatAvailability(1)

  if (daysWithAtLeastOneSeat.length == 0) {
    // return "We're sorry, there are no longer any spots available for this workshop"
    return false
  }

  if (classParticipants.value || classParticipants.value > 0) {
    let list = daysWithSeatAvailability(classParticipants.value)
    if (list.length === 0) {
      return `We're sorry. We don't have ${classParticipants.value} sequential seats available for this workshop.`
    }
  }
  return false
})

const availableSlots = computed(() => {
  if (
    !isMakeItNow.value ||
    !classDetails.value.daySales ||
    !classDetails.value.classDays
  ) {
    return []
  }

  if (!classParticipants.value || classParticipants.value <= 0) {
    return []
  }

  const requiredSeats = classParticipants.value

  return daysWithSeatAvailability(requiredSeats)
})

const canAddClassToCart = (classDetails, isEntireSeries = true) => {
  let ok = true
  ok = ok && isEntireSeries ? classDetails.canAddEntireSeries : true
  ok = ok && !classDetails.isPastCutOffDate
  ok =
    ok &&
    (!classDetails.displayOnly ||
      (classDetails.displayOnly && (userStore.isPreAuthorized || classStore.userInWhitelist(classDetails, false))))
  // ok = ok && !classDetails.daySales // XXX hack
  ok = ok && classStore.userInWhitelist(classDetails)
  ok = ok && !classDetails.classIsFull // (classDetails.seatsAvailable > 0)
  ok = ok && cartStore.seatAvailableIncludingCart(classDetails)

  if (classDetails.membersOnly) {
    ok =
      ok &&
      (cartStore.haveMembershipInCart ||
        (userStore.account && userStore.memberWithActiveMembership))
  }
  const hasStarted = classDetails.hasStarted && !classDetails.startsToday

  const allowRegistrationForStartedClasses = isEntireSeries 
    ? siteConfig.features.allowRegistrationForStartedClasses 
    : true  
  if (hasStarted && isEntireSeries) {
    ok = ok && allowRegistrationForStartedClasses
    if (!ok) {
      console.log('Class has started and allowRegistrationForStartedClasses is set to: ', allowRegistrationForStartedClasses)
    }
  }
  ok = ok && !(hasStarted && !classDetails.daySales && !allowRegistrationForStartedClasses)

  if (
    ok &&
    classDetails.daySales &&
    classDetails.classDays.length !== daysWithSeatAvailability(1).length
  ) {
    ok = false
  }

  if (siteConfig.features.payWhatYouCan && classDetails.promoCode == 'PWYC' && !priceSelected.value) {
    ok = false
    ctaError.value = 'Please select a price before adding to cart'
    return false
  }

  if (formRequired.value && !classDetails.formData) {
    ok = false
    ctaError.value = 'Please fill out the form before adding to cart'
    return false
  }

  return ok
}

const requiresMembership = computed(() => {
  return (
    classDetails.value &&
    classDetails.value.membersOnly &&
    !cartStore.haveMembershipInCart &&
    !(userStore.account && userStore.isActiveMember)
  )
})

const canAddToWaitlist = (classDetails) => {
  let ok = true
  ok = ok && classDetails.waitList
  ok = ok && !classDetails.daySales // XXX hack
  ok =
    ok &&
    (classDetails.classIsFull ||
      (classDetails.displayOnly &&
        siteConfig.features?.showJoinWaitlistForDisplayOnlyClass))
  if (classDetails.membersOnly) {
    ok =
      ok &&
      (cartStore.haveMembershipInCart ||
        (userStore.account && userStore.isActiveMember))
  }
  ok = ok && !classDetails.hasStarted
  return ok
}

// const slotsTooltipShown = ref(false)

const spotsLeftText = (spots) => {
  if (spots === 1) {
    return `1 ${siteConfig.copy?.spotLeft || 'spot left'}`
  }
  return `${spots} ${siteConfig.copy?.spotsLeft || 'spots left'}`
}

const totalLabel = computed(() => {
  if (isMakeItADate.value) {
    return classParticipants.value === 1
      ? '1 couple'
      : `${classParticipants.value} couples`
  } else if (isMakeItNow.value) {
    return classParticipants.value === 1
      ? '1 person'
      : `${classParticipants.value} people`
  }
  return ''
})

const selectedSessions = ref([])

const housingSelection = ref(null)
const mealSelection = ref(null)

const handleHousingSelection = (payload) => {
  console.log('handleHousingSelection', payload)
  if (payload === null) {
    housingSelection.value = null
  } else {
    housingSelection.value = payload
  }
}

const handleMealsSelection = (payload) => {
  console.log('handleMealsSelection', payload)
  if (payload === null) {
    mealSelection.value = null
  } else {
    mealSelection.value = payload
  }
}

const handleFormData = (payload) => {
  classDetails.value.formData = payload
}

const formRequired = computed(() => {
  return classDetails.value.relatedQuestionnaire 
    && (classDetails.value.formRequired || siteConfig.features.requireFilledForm)
})

const backToClasses = () => {
  const backPath = window?.history?.state?.back
  const currentPath = `/${currentLabel.value}`

  if (backPath?.includes(currentPath)) {
    window.history.back()
  } else {
    router.push({ path: currentPath })
  }
}

const canShowShowFarmHousingOptions = computed(() => {
  return (siteConfig.key === 'snowfarm') && canAddClassToCart(classDetails.value) && classDetails.value.catalogId !== 2833
})

const mediumText = computed(() => {
  let mediums = [classDetails.value.medium, classDetails.value.medium1, classDetails.value.medium2]
  if (siteConfig.features.preferClassMediums) {
    mediums = [classDetails.value.medium1, classDetails.value.medium2, classDetails.value.medium]
  }
  return mediums.find((m) => m !== 'None') || ''
})

const { prefixTitle } = useTitle()
watchEffect(() => {
  if (classDetails.value) prefixTitle(classDetails.value.shortTitle)
})

</script>

<template>
  <div class="container">
    <div
      v-if="!classDetails"
      class="h-[calc(100vh-280px)] md:h-[calc(100vh-320px)] xl:h-[calc(100vh-385px)] flex justify-center items-center"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" style="
          margin: auto;
          background: rgb(255, 255, 255);
          display: block;
          shape-rendering: auto;
        " width="150px" height="150px" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid"
      >
        <path d="M10 50A40 40 0 0 0 90 50A40 42 0 0 1 10 50" class="fill-accent-1" stroke="none">
          <animateTransform
            attributeName="transform" type="rotate" dur="1s" repeatCount="indefinite" keyTimes="0;1"
            values="0 50 51;360 50 51"
          ></animateTransform>
        </path>
      </svg>
    </div>
    <section class="class-details" v-if="classDetails">
      <div class="flex justify-between">
        <button @click="backToClasses" class="go-back-link btn-accent-1 small-bold">
          <ChevronLeftIcon class="icon" />
          Go Back to
          {{ capitalizeFirstLetter(currentLabel) }}
        </button>
        <ShareBar />
      </div>


      <div
        class="class-box" :class="{
          'has-sessions': classDetails.daySales && classDetails.sessions,
        }"
      >
        <!-- IMAGE(S) BLOCK -->
        <div class="images-wrapper">
          <div class="main-image">
            <img v-lazy="selectedImage" />
            <div
              class="left-spots-overlay xlarge-text xl:heading-3" v-if="
                !classDetails.hasStarted &&
                  !isMakeItNow &&
                  !classDetails.daySales &&
                  siteConfig.features.showSpotsLeftThreshold &&
                  classDetails.seatsAvailable <=
                  siteConfig.features.showSpotsLeftThreshold &&
                  classDetails.seatsAvailable > 0
              "
            >
              {{ spotsLeftText(classDetails.seatsAvailable) }}
            </div>
            <div class="waitlist-overlay" v-if="classDetails.classIsFull && !siteConfig.features?.hideClassFullMessage">
              <h4 class="xlarge-text xl:heading-3">Class Full</h4>
              <span
                class="small-regular xl:large-regular" v-if="
                  classDetails.waitList &&
                    !classDetails.isPastCutOffDate &&
                    !classDetails.hasStarted
                "
              >waitlist only</span>
            </div>
            <div class="image-overlay" v-if="!siteConfig.features?.hideClassImageOverlay">
              <h5
                class="xlarge-text xl:heading-3 -mb-1" v-html="mediumText"
              ></h5>
              <span v-if="!siteConfig.features.hideLocationTypeInOverlay" v-html="classDetails.locationType"></span>
            </div>
          </div>

          <div class="alt-images" v-if="classDetails.images.length > 1">
            <button
              v-for="(image, idx) in classDetails.images" :key="image" :class="{
                selected:
                  selectedImage === image || (!selectedImage && idx === 0),
              }" @click="selectedImageIndex = idx"
            >
              <img v-lazy="image" />
            </button>
          </div>
        </div>

        <!-- THIS BLOCK IS FOR A NORMAL CLASS -->
        <!-- It is hidden if class is Make-It-Now or supports Partial Sales (daySales) -->
        <!-- <div class="class-info" v-if="!isMakeItNow && !classDetails.daySales" :class="{'xl:!hidden': isMakeItNow || classDetails.daySales }"> -->
        <div
          class="class-info" v-if="!isMakeItNow && !classDetails.daySales"
          :class="{ 'xl:!hidden': isMakeItNow || classDetails.daySales }"
        >
          <div class="row">
            <ClassSummary :classDetails="classDetails" />
          </div>

          <!-- <div class="wrapper mt-5" v-if="isMakeItNow">
            <button class="outlined-accent-1 btn-slots" @click="haveAvailableSlots = true">See available slots</button>
          </div> -->
          <!-- TOOLTIP IS HERE :) -->
          <!-- <div class="wrapper mt-5" v-if="isMakeItNow">
            <div class="custom-tooltip">
              <span class="inline-block" @mouseenter="slotsTooltipShown = true" @mouseleave="slotsTooltipShown = false">
                <button class="outlined-accent-1 btn-slots" @click="haveAvailableSlots = true" disabled>See available slots</button>
              </span>
              <transition name="fade">
                <span v-if="slotsTooltipShown" class="tooltip">Please enter number of participants first</span>
              </transition>
            </div>
          </div> -->
          <div class="wrapper">
            <div v-if="siteConfig.features.payWhatYouCan && classDetails.promoCode == 'PWYC'">
              <PayWhatYouCan @update-selected-payment="updateSelectedPayment" />
              <h2 v-html="siteConfig.features.payWhatYouCan.description"></h2>
            </div>
            <div v-else>
              <div class="class-price" v-if="!siteConfig.features.emphasizeMemberPrice">
                <h3 class="main-price xlarge-text xl:heading-2" v-if="!siteConfig.features.normalizePricing">
                  &dollar;{{ classDetails.costNon.toFixed(2) }}
                </h3>
                <h4
                  v-else
                  class="members-price xl:heading-3"
                >
                  &dollar;{{ classDetails.costNon.toFixed(2) }}
                  <span class="small-regular">(non-members)</span>
                </h4>
                <h4 class="members-price xl:heading-3" v-if="classDetails.costNon != classDetails.costMember">
                  &dollar;{{ classDetails.costMember.toFixed(2) }}
                  <span class="small-regular lg:body-regular">(members)</span>
                </h4>

                <h4 class="members-price xl:heading-4" v-if="classDetails.fee && classDetails.fee != 0">
                  + &dollar;{{ classDetails.fee.toFixed(2) }}
                  <span class="small-regular lg:body-regular">({{
                    classDetails.feeType || 'supply fee'
                  }})</span>
                </h4>
              </div>
              <div class="class-price" v-if="siteConfig.features.emphasizeMemberPrice">
                <h3 class="main-price xlarge-text xl:heading-2">
                  &dollar;{{ classDetails.costMember.toFixed(2) }}
                </h3>
                <h4 class="members-price xl:heading-3" v-if="classDetails.costNon != classDetails.costMember">
                  &dollar;{{ classDetails.costNon.toFixed(2) }}
                  <span class="small-regular lg:body-regular">(non-members)</span>
                </h4>

                <h4 class="members-price xl:heading-4" v-if="classDetails.fee && classDetails.fee != 0">
                  + &dollar;{{ classDetails.fee.toFixed(2) }}
                  <span class="small-regular lg:body-regular">({{
                    classDetails.feeType || 'supply fee'
                  }})</span>
                </h4>
              </div>

              <!-- Snow Farm Stuff -->
              <HousingMealsSection
                :classDetails="classDetails" :housingSelection="housingSelection"
                :mealSelection="mealSelection" @selectHousing="handleHousingSelection"
                @selectMeals="handleMealsSelection" v-if="canShowShowFarmHousingOptions"
              />
            </div>

            <div v-if="classDetails.relatedQuestionnaire" class="my-10">
              <Form2
                :form-id="classDetails.relatedQuestionnaire" :related-id="classDetails.catalogId" :handle-form-data="handleFormData"
                :submit-on-change="true"
                :back-to="`/classes/${classDetails.catalogId}`"
              />
            </div>

            <p v-if="classDetails.comments" class="py-2 font-bold">{{ classDetails.comments }}</p>

            <div v-if="classDetails.allowDeposit" class="my-4">
              <label class="flex items-center space-x-2">
                <input type="checkbox" v-model="classDetails.payDepositOnly" class="form-checkbox">
                <span>Pay deposit only</span>
              </label>
            </div>

            <button
              v-if="canAddClassToCart(classDetails)" class="btn-accent-1 btn-cart"
              @click="addToCart(classDetails.catalogId)"
            >
              Add to Cart
              <span v-if="classDetails.payDepositOnly"> (Deposit)</span>
            </button>
            <div v-else-if="requiresMembership && !classDetails.displayOnly">
              <router-link to="/memberships" v-if="siteConfig.modules.memberships">
                <button class="btn-accent-1 btn-cart">
                  Become a Member to Register
                </button>
              </router-link>
              <div class="text-accent-1 font-bold" v-else>
                Membership Required
              </div>
            </div>
            <div
              class="py-2 font-bold" v-else-if="
                cartStore.duplicatesOfClass(classDetails)?.length &&
                  !cartStore.seatAvailableIncludingCart(classDetails)
                  && !classDetails.isPastCutOffDate
              "
            >
              All available seats are in the cart.
            </div>
            <button
              v-else-if="
                canAddToWaitlist(classDetails) && !classDetails.isPastCutOffDate
              " class="btn-accent-1 btn-cart" @click="joinWaitlist(classDetails.catalogId)"
            >
              Join the waitlist
            </button>
            <div
              class="py-2 font-bold" v-else-if="
                classDetails.seatsAvailable <= 0 &&
                  !classDetails.isPastCutOffDate && !siteConfig.features?.hideClassFullMessage
              "
            >
              This class is full.
            </div>
            <div class="py-2 font-bold" v-else-if="classDetails.isPastCutOffDate">
              Registration for this class is now closed.
            </div>
            <div
              v-else-if="
                classDetails.displayOnly &&
                  siteConfig.copy?.registrationOpens &&
                  !(
                    siteConfig.key === 'snowfarm' &&
                    classDetails.shortTitle.includes('Residency')
                  )
              " class="py-2 text-accent-1 font-bold" v-html="siteConfig.copy.registrationOpens"
            ></div>
            <div v-else class="py-2 font-bold" v-html="ctaError || siteConfig.copy?.classes?.registrationNotAvailable || 'Registration is not available for this class.'"></div>
          </div>
        </div>

        <!-- THIS BLOCK IS FOR A CLASSES WITH PARTIAL SALES THAT ARE NOT PGC MAKE-IT-NOW CLASSES -->
        <div class="class-info xl:!flex" v-if="classDetails.daySales && !isMakeItNow">
          <div class="row">
            <ClassSummary :classDetails="classDetails" />
          </div>
          <div class="wrapper" v-if="!siteConfig.features.emphasizeMemberPrice">
            <div
              class="class-price" v-if="
                siteConfig.features.showFullRegistrationOptionForPartialSales && classDetails.canAddEntireSeries
              "
            >
              <h3 class="main-price xlarge-text xl:heading-2">
                &dollar;{{ classDetails.costNon.toFixed(2) }}
                <span class="small-regular lg:body-regular">for entire series</span>
              </h3>
              <h4
                class="members-price xl:heading-3" v-if="
                  classDetails.costNon.toFixed(2) !=
                    classDetails.costMember.toFixed(2)
                "
              >
                &dollar;{{ classDetails.costMember.toFixed(2) }}
                <span class="small-regular lg:body-regular">(members)</span>
              </h4>
              <h4 class="members-price xl:heading-4" v-if="classDetails.fee && classDetails.fee != 0">
                + &dollar;{{ classDetails.fee.toFixed(2) }}
                <span class="small-regular lg:body-regular">({{
                  classDetails.feeType || 'supply fee'
                }})</span>
              </h4>
            </div>
          </div>
          <div class="wrapper" v-if="!siteConfig.features.emphasizeMemberPrice">
            <div class="class-price">
              <h3 class="main-price xlarge-text xl:heading-2">
                &dollar;{{ classDetails.sessionCostNon.toFixed(2) }}
                <span
                  v-if="
                    siteConfig.features
                      .showFullRegistrationOptionForPartialSales
                  " class="small-regular lg:body-regular"
                >per session</span>
              </h3>
              <h4
                class="members-price xl:heading-3" v-if="
                  classDetails.sessionCostNon.toFixed(2) !=
                    classDetails.sessionCostMem.toFixed(2)
                "
              >
                &dollar;{{ classDetails.sessionCostMem.toFixed(2) }}
                <span class="small-regular lg:body-regular">(members)</span>
              </h4>
              <h4
                class="members-price xl:heading-4" v-if="
                  classDetails.feePerSession && classDetails.feePerSession != 0
                "
              >
                + &dollar;{{ classDetails.feePerSession.toFixed(2) }}
                <span class="small-regular lg:body-regular">({{
                  classDetails.feeType || 'supply fee'
                }})</span>
              </h4>
            </div>
          </div>
          <div class="wrapper" v-if="siteConfig.features.emphasizeMemberPrice && classDetails.canAddEntireSeries">
            <div class="text-sm xl:mt-2 font-bold">Entire series</div>
            <div class="class-price xl:!mt-0">
              <h3 class="main-price xlarge-text xl:heading-2">
                &dollar;{{ classDetails.memberCostFmt }}
              </h3>
              <h4
                class="members-price xl:heading-3" v-if="
                  classDetails.costNon.toFixed(2) !=
                    classDetails.costMember.toFixed(2)
                "
              >
                &dollar;{{ classDetails.costNon.toFixed(2) }}
                <span class="small-regular lg:body-regular">(non-members)</span>
              </h4>
            </div>
          </div>
          <div class="wrapper" v-if="siteConfig.features.emphasizeMemberPrice">
            <div class="text-sm xl:mt-2 font-bold">Per session</div>
            <div class="class-price xl:!mt-0">
              <h3 class="main-price xlarge-text xl:heading-2">
                &dollar;{{ classDetails.sessionCostMem.toFixed(2) }}
              </h3>
              <h4
                class="members-price xl:heading-3" v-if="
                  classDetails.sessionCostNon.toFixed(2) !=
                    classDetails.sessionCostMem.toFixed(2)
                "
              >
                &dollar;{{ classDetails.sessionCostNon.toFixed(2) }}
                <span class="small-regular lg:body-regular">(non-members)</span>
              </h4>
            </div>
          </div>
        </div>

        <!-- THIS BLOCK IS FOR PGC MAKE-IT-NOW CLASSES -->
        <div class="class-info xl:!flex" v-if="isMakeItNow">
          <div class="row">
            <ClassSummary :classDetails="classDetails" />
          </div>
          <div class="wrapper">
            <div class="class-price">
              <h3 class="main-price xlarge-text xl:heading-2">
                &dollar;{{ classDetails.sessionCostNon.toFixed(2) }}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div class="detailed-information" v-if="siteConfig.features.showDescriptionFirst">
        <div class="program-description">
          <h3 class="section-heading xlarge-text xl:heading-3">Description</h3>
          <p class="markup" v-html="classDetails.description"></p>
          <p v-if="classDetails.supplyUrl">
            <a target="_blank" class="text-accent-1 font-bold" :href="classDetails.supplyUrl">{{
              siteConfig?.copy?.supplyListAltCTA || 'View Supply List'
            }}</a>
          </p>
        </div>
      </div>

      <div
        class="detailed-information class-box" :class="{
          'has-sessions': classDetails.daySales && classDetails.sessions,
        }" v-if="classDetails.daySales && !isMakeItNow"
      >
        <!-- THIS BLOCK IS FOR A CLASSES WITH PARTIAL SALES THAT ARE NOT PGC MAKE-IT-NOW CLASSES -->
        <div class="sessions-selection">
          <div class="available-slots" v-if="!siteConfig.features.useCheckboxGridForPartialSales">
            <div class="slots-wrapper">
              <div class="class-slots">
                <header class="extra-small-bold">
                  <h5></h5>
                  <h5>Session Date</h5>
                  <h5>Day</h5>
                  <h5>Time</h5>
                  <h5>Seats Available</h5>
                </header>

                <div class="slot-row" v-for="(slot, index) in classDetails.classDays" :key="index">
                  <input
                    v-if="!classDetails.displayOnly" type="radio" name="singleSelectedSlot" :value="index"
                    v-model="singleSelectedSlot"
                  />
                  <div v-else />
                  <div class="box">
                    <span class="box-label">Date</span>
                    <h4 v-html="usDateFormatToShortMonthAndDay(slot.dateFmt)"></h4>
                  </div>
                  <div class="box">
                    <span class="box-label">Day</span>
                    <span v-html="slot.classDay"></span>
                  </div>
                  <div class="box">
                    <span class="box-label">Time</span>
                    <span v-html="`${slot.startTimeFmt} - ${slot.endTimeFmt}`"></span>
                  </div>
                  <div class="box">
                    <span class="box-label">Seats Available</span>
                    <span
                      v-html="`${greaterOf(slot.limit - slot.daysSold, 0)} of ${slot.limit
                      } seats available`
                      "
                    ></span>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="classDetails.relatedQuestionnaire" class="my-10">
              <Form2
                :form-id="classDetails.relatedQuestionnaire" :related-id="classDetails.catalogId" :handle-form-data="handleFormData"
                :submit-on-change="true"
                :back-to="`/classes/${classDetails.catalogId}`"
              />
              <div v-if="formRequired && !classDetails.formData" class="py-2 font-bold">Please fill out the form before adding to cart</div>
            </div>

            <p v-if="classDetails.comments" class="py-2 font-bold">{{ classDetails.comments }}</p>

            <div v-if="classDetails.allowDeposit" class="my-4">
              <label class="flex items-center space-x-2">
                <input type="checkbox" v-model="classDetails.payDepositOnly" class="form-checkbox">
                <span>Pay deposit only</span>
              </label>
            </div>
            <!-- v-html="classDetails.inCart ? 'In Cart' : 'Add To Cart' " -->
            <template v-if="!classDetails.displayOnly && singleSelectedSlot !== null">
              <div
                class="py-2 font-bold" v-if="
                  !cartStore.seatAvailableIncludingCart(classDetails, classDetails.classDays[singleSelectedSlot].scheduleId) 
                    && classDetails.classDays[singleSelectedSlot].limit -
                      classDetails.classDays[singleSelectedSlot].daysSold > 0
                "
              >
                All available seats are in the cart.
              </div>
              <button
                v-else-if="
                  classDetails.classDays[singleSelectedSlot].limit -
                    classDetails.classDays[singleSelectedSlot].daysSold >
                    0
                " class="btn-accent-1 btn-cart" :disabled="classDetails.inCart" @click="
                  addClassDayToCart(
                    classDetails.catalogId,
                    classDetails.classDays[singleSelectedSlot].scheduleId,
                    classDetails
                  )
                "
              >
                Add to Cart
              </button>
              <button
                v-else-if=" 
                  canAddToWaitlist(classDetails) &&
                    classDetails.classDays[singleSelectedSlot].limit -
                    classDetails.classDays[singleSelectedSlot].daysSold <=
                    0
                " class="btn-accent-1 btn-cart" @click.prevent="
                  addWaitlistClassDayToCart(
                    classDetails.catalogId,
                    classDetails.classDays[singleSelectedSlot].scheduleId
                  )
                "
              >
                Join the waitlist
              </button>
              <div
                class="py-2 font-bold" 
                v-else-if="
                  !canAddToWaitlist(classDetails) &&
                    classDetails.classDays[singleSelectedSlot].limit -
                    classDetails.classDays[singleSelectedSlot].daysSold <=
                    0
                "
              >
                No available seats.
              </div>
            </template>
            <button
              v-if="!siteConfig.features.hideEntireSeries && canAddClassToCart(classDetails)" @click="addToCart(classDetails.catalogId)"
              class="btn-accent-1 btn-cart md:ml-4"
            >
              Add Entire Series to Cart
            </button>
          </div>
          <div class="available-slots" v-if="siteConfig.features.useCheckboxGridForPartialSales && canAddClassToCart(classDetails, false)">
            <div class="slots-wrapper">
              <div class="class-slots">
                <header class="small-bold !flex">
                  <h5 class="!block">Available Dates</h5>
                </header>

                <div class="flex flex-col sm:flex-row flex-wrap gap-x-14 sm:gap-y-7 !p-0 sm:!p-5">
                  <label
                    class="date-slot-box small-bold" v-for="(classDay, index) in classDetails.classDays"
                    :key="index"
                  >
                    <input
                      type="checkbox" name="selectedSessions" :value="classDay.scheduleId"
                      v-model="selectedSessions" class="!w-5 !h-5"
                      :disabled="classDay.limit - classDay.daysSold - (classStore.classRegistrationCountsById[classDetails.catalogId] || 0) <= 0"
                    />
                    <h4
                      :class="{
                        'text-secondary-text':
                          classDay.limit - classDay.daysSold <= 0,
                      }"
                    >
                      <span
                        v-html="usDateFormatToShortMonthAndDay(classDay.dateFmt)
                        "
                      ></span>
                      <span
                        v-if="classDay.limit - classDay.daysSold - (classStore.classRegistrationCountsById[classDetails.catalogId] || 0) <= 0"
                        class="font-normal ml-0.5"
                      >(full)</span>
                    </h4>
                  </label>
                </div>

                <footer class="small-regular !bg-gray-50 !px-7">
                  <p>{{ selectedSessions.length }} session(s) selected</p>
                  <div class="inline-block">
                    <strong>Total:</strong>
                    <span>
                      &dollar;{{
                        (
                          selectedSessions.length * classDetails.sessionCostMem
                        ).toFixed(2)
                      }}
                      <template
                        v-if="selectedSessions.length > 0 && (classDetails.sessionCostMem !== classDetails.sessionCostNon)"
                      >
                        (&dollar;{{
                          (
                            selectedSessions.length * classDetails.sessionCostNon
                          ).toFixed(2)
                        }}
                        for non-members)
                      </template>
                    </span>
                  </div>
                </footer>
              </div>
            </div>
            <div v-if="classDetails.relatedQuestionnaire" class="my-10">
              <Form2
                :form-id="classDetails.relatedQuestionnaire" :related-id="classDetails.catalogId" :handle-form-data="handleFormData"
                :submit-on-change="true"
                :back-to="`/classes/${classDetails.catalogId}`"
              />
            </div>

            <p v-if="classDetails.comments" class="py-2 font-bold">{{ classDetails.comments }}</p>

            <div v-if="classDetails.allowDeposit" class="my-4">
              <label class="flex items-center space-x-2">
                <input type="checkbox" v-model="classDetails.payDepositOnly" class="form-checkbox">
                <span>Pay deposit only</span>
              </label>
            </div>

            <button
              v-if="canAddClassToCart(classDetails, false)"
              class="btn-accent-1 btn-cart" :disabled="selectedSessions.length < 1" @click="
                addClassDaysToCart(classDetails.catalogId, selectedSessions, classDetails)
              "
            >
              Add
              {{
                selectedSessions.length > 1
                  ? `${selectedSessions.length} Sessions`
                  : `Session`
              }}
              to Cart
            </button>
            <button
              v-if="!siteConfig.features.hideEntireSeries && canAddClassToCart(classDetails)" @click="addToCart(classDetails.catalogId)"
              class="btn-accent-1 btn-cart md:ml-4"
            >
              Add Entire Series to Cart
            </button>

            <!-- XXX <button
              v-if="(classDetails.classDays[singleSelectedSlot].limit - classDetails.classDays[singleSelectedSlot].daysSold) <= 0"
              class="btn-accent-1 btn-cart"
              @click.prevent="addWaitlistClassDayToCart(classDetails.catalogId, classDetails.classDays[singleSelectedSlot].scheduleId)"
            >Join the waitlist</button> -->
          </div>
        </div>
      </div>

      <div
        class="detailed-information class-box" :class="{
          'has-sessions': classDetails.daySales && classDetails.sessions,
        }" v-if="isMakeItNow"
      >
        <!-- THIS BLOCK IS FOR PGC MAKE-IT-NOW CLASSES -->
        <div class="make-it-now">
          <div class="class-info">
            <div class="row" v-if="daysWithSeatAvailability(1).length > 0">
              <div class="participants">
                <b class="text-xl">Step 1: Number of
                  {{ isMakeItADate ? 'Couples' : 'Participants' }}</b>
                <input
                  type="number" v-model="classParticipants" placeholder="eg. 4" class="form-control"
                  @input="checkParticipantsNumber"
                />
              </div>
              <p class="mb-5" v-if="isMakeItADate">
                One ticket ($95) per couple. Enter the number of tickets you
                would like to reserve in this box. For just one couple, enter 1.
                This will show you which time slots are available. If you are
                reserving space for more than one couple, enter the total number
                of couples (for 6 people, you would enter "3"). Time slots are
                booked one after another, so everyone in your group can
                participate, watch, and take pictures together during your
                activity!
              </p>
              <p class="mb-5" v-if="!isMakeItADate">
                Enter the number of participants you would like to register in
                this box. This will show you which timeslots are available for
                your group. When registering for multiple participants, your
                time slots are booked one after another, so everyone in your
                group can participate, watch, and take pictures together during
                your activity!
              </p>
              <p class="!block">
                Have questions? Call us at
                <a class="text-accent-1" href="tel:412-365-2145">412-365-2145</a>
                and we can help!
              </p>
            </div>
          </div>
          <div v-if="noSlotsAvailableMessage">
            <strong v-html="noSlotsAvailableMessage"></strong>
          </div>
          <!-- MAKE-IT-NOW AVAILABLE SLOTS -->
          <div class="available-slots mt-3" v-if="availableSlots && availableSlots.length > 0 && !isLargeGroup">
            <b class="text-xl">Step 2: Choose your time</b>
            <div class="slots-wrapper">
              <div class="class-slots">
                <header class="extra-small-bold">
                  <h5></h5>
                  <h5>Date</h5>
                  <h5>Day</h5>
                  <h5>Time</h5>
                  <h5>Slots</h5>
                </header>

                <div class="slot-row" v-for="(slot, index) in availableSlots" :key="index">
                  <input type="radio" name="singleSelectedSlot" :value="index" v-model="singleSelectedSlot" />
                  <div class="box">
                    <span class="box-label">Date</span>
                    <h4 v-html="slot.date"></h4>
                  </div>
                  <div class="box">
                    <span class="box-label">Day</span>
                    <span v-html="slot.day"></span>
                  </div>
                  <div class="box">
                    <span class="box-label">Time</span>
                    <span>{{ slot.time }}</span>
                  </div>
                  <div class="box">
                    <span class="box-label">Slots</span>
                    <span
                      v-if="classParticipants && classParticipants > 1" v-html="`${classParticipants} ${isMakeItADate ? 'couple' : 'people'
                      } (${slot.duration} min/ea.)`
                      "
                    ></span>
                    <span
                      v-if="classParticipants && classParticipants === 1" v-html="`1 ${isMakeItADate ? 'couple' : 'person'} (${slot.duration
                      } min)`
                      "
                    ></span>
                  </div>
                </div>

                <footer class="small-regular">
                  <strong>{{ totalLabel }}:
                    <span>&dollar;{{
                      (
                        classParticipants * classDetails.sessionCostNon
                      ).toFixed(2)
                    }}</span></strong>
                  <p><!-- {{ availableSlots.length }} slots available --></p>
                  <!-- <strong>{{classParticipants}} Person Rate: <span>&dollar;{{ (classParticipants * classDetails.costNon).toFixed(2) }}</span> </strong> -->
                </footer>
              </div>
            </div>

            <!-- v-html="classDetails.inCart ? 'In Cart' : 'Add To Cart' " -->
            <button
              class="btn-accent-1 btn-cart" :disabled="classDetails.inCart" @click="
                addMakeItNowToCart(
                  classDetails.catalogId,
                  availableSlots[singleSelectedSlot].classDays
                )
              "
            >
              Add to Cart
            </button>
          </div>

          <!-- <BaseModal v-if="showContactModal">
            <div class="contact-modal">
              <p v-if="isLargeGroup">
                For more than 6 participants, please call us at
                <a class="text-accent-1" href="tel:412-365-2145">412-365-2145</a>
                to register.
              </p>
              <p v-else>
                There are not enough available time slots for the number of
                participants you entered. <br />
                Please call us at
                <a class="text-accent-1" href="tel:412-365-2145">412-365-2145</a>
                to register.
              </p>
              <button class="btn-accent-1" @click="showContactModal = false">
                Got it
              </button>
            </div>
          </BaseModal> -->
          <TransitionRoot appear :show="showContactModal" as="template">
            <Dialog as="div" @close="showContactModal = false" class="base-modal !fixed !h-full !bg-transparent">
              <TransitionChild
                as="template" enter="duration-300 ease-out" enter-from="opacity-0" enter-to="opacity-100"
                leave="duration-200 ease-in" leave-from="opacity-100" leave-to="opacity-0"
              >
                <div class="backdrop !bg-black/40 !px-4" aria-hidden="true">
                  <div class="container">
                    <TransitionChild
                      as="template" enter="duration-300 ease-out" enter-from="opacity-0 scale-95"
                      enter-to="opacity-100 scale-100" leave="duration-200 ease-in"
                      leave-from="opacity-100 scale-100"
                      leave-to="opacity-0 scale-95"
                    >
                      <div class="modal-body !bg-white !w-max !max-w-full">
                        <DialogPanel class="p-5 pb-6 max-w-[26rem] text-center">
                          <p class="mb-5 block text-primary-text" v-if="isLargeGroup">
                            For more than 6 participants, please call us at
                            <a class="text-accent-1" href="tel:412-365-2145">412-365-2145</a> to register.
                          </p>
                          <p class="mb-5 block text-primary-text" v-else>
                            There are not enough available time slots for the number of participants you entered. <br>
                            Please call us at <a class="text-accent-1" href="tel:412-365-2145">412-365-2145</a> to
                            register.
                          </p>
                          <button class="btn-accent-1 py-1.5 w-44 rounded-md" @click="showContactModal = false">
                            Got
                            it
                          </button>
                        </DialogPanel>
                      </div>
                    </TransitionChild>
                  </div>
                </div>
              </TransitionChild>
            </Dialog>
          </TransitionRoot>
        </div>
      </div>

      <div class="detailed-information xl:min-h-[150px]">
        <div class="program-description" v-if="!siteConfig.features.showDescriptionFirst">
          <h3 class="section-heading xlarge-text xl:heading-3">Description</h3>
          <p class="markup" v-html="classDetails.description"></p>
          <p v-if="classDetails.supplyUrl">
            <a target="_blank" class="text-accent-1 font-bold" :href="classDetails.supplyUrl">{{
              siteConfig?.copy?.supplyListAltCTA || 'View Supply List'
            }}</a>
          </p>
        </div>

        <div class="instructors" v-if="instructors.length">
          <h3 class="section-heading xlarge-text xl:heading-3">
            {{ instructors.length > 1 ? 'Instructors' : 'Instructor' }}
          </h3>
          <div class="instructor-box" v-for="(instructor, index) in instructors" :key="index">
            <img
              v-if="instructor?.imageUrl" v-lazy="instructor?.imageUrl || siteConfig.assets?.defaultAvatarImage
              "
            />
            <img v-if="!instructor?.imageUrl" v-lazy="`/sites/${siteConfig.key}/placeholder-avatar.jpg`" />
            <div class="info" v-if="instructor.bio">
              <h5 v-html="instructor?.fullName"></h5>
              <ul>
                <li class="markup" v-html="cleanMarkup(instructor.bio)"></li>
              </ul>
              <router-link
                v-if="siteConfig.modules.instructors" :to="`/instructors/${instructor.instructorId}`"
                class="profile-link"
              >
                Learn More
                <ArrowRightIcon class="icon" />
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <div class="clear-both"></div>
</template>

<style>
@import url('./index.pcss');
.program-description p {
  margin-bottom: 12px;
}
</style>
